import { computed, defineComponent, ref } from "vue";
import { LCircle } from "vue2-leaflet";
import { LatLng } from "leaflet";
import ShapeEnum from "@/pages/wellPlate/_providers/types/enums/ShapeEnum";
import { useExperimentConfigurations } from "@/pages/wellPlate/_providers/withExperimentConfigurations";
import { useVirtualWellplate } from "@/pages/wellPlate/_providers/VirtualWellPlateShapeProvider/withVirtualWellplate";
import { useExperiment } from "@/pages/wellPlate/_providers/withExperiment";
import { useGroups } from "@/pages/wellPlate/_providers/withGroups/withGroups";
import { useSelect } from "@/pages/wellPlate/_providers/withSelect/withSelect";
import { getVirtualStrokeColor, getStrokeWeight } from "./index";
import ROI from "./ROI.vue";
import { useConfluence } from "@/pages/wellPlate/_providers/withConfluence";
import { useScans } from "@/pages/wellPlate/_providers/withScans/withScans";
import { useAlerts } from "@/pages/wellPlate/_providers/withAlerts/withAlerts";
export default defineComponent({
    components: {
        LCircle,
        ROI
    },
    props: {
        shape: {
            type: Object,
            required: true
        },
        enabledGroupedAreasInteraction: {
            type: Boolean,
            required: false,
            default: true
        },
        ignoreGroups: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: ["openLux3Experiment"],
    setup(props, { emit }) {
        const onHover = ref(false);
        const virtualWellPlateProvider = useVirtualWellplate();
        const { isFullStitch, experiment } = useExperiment();
        const { getGroupByAreasIds } = useGroups();
        const selector = useSelect();
        const { algorithms } = useExperimentConfigurations();
        const confluenceProvider = useConfluence();
        const rois = computed(() => virtualWellPlateProvider.getAllROIsInWell(props.shape.id));
        const { alert, showAlert } = useAlerts();
        const { completedScanTimepoints } = useScans();
        const hasAlertConditionMet = computed(() => {
            if (!isFullStitch.value || !alert.value || !algorithms.Confluence) {
                return false;
            }
            const latestCompletedScanTimepoint = completedScanTimepoints.value[completedScanTimepoints.value.length - 1];
            const confluence = confluenceProvider.confluence.value[props.shape.id].Brightfield.Data[latestCompletedScanTimepoint];
            return (alert.value.HasBeenTriggered &&
                (alert.value.OverUnderAlgorithmTargetValue
                    ? confluence >= alert.value.AlgorithmTargetValue
                    : confluence <= alert.value.AlgorithmTargetValue));
        });
        const selected = computed(() => {
            if (props.shape.type === ShapeEnum.WELL) {
                return rois.value.every(roi => selector.selectedAreasIds.value.includes(roi.id));
            }
            return selector.selectedAreasIds.value.includes(props.shape.id);
        });
        const grouped = computed(() => {
            if (isFullStitch.value) {
                return !!getGroupByAreasIds([props.shape.id]);
            }
            return !!getGroupByAreasIds(rois.value.map(roi => roi.id));
        });
        const interactive = computed(() => props.enabledGroupedAreasInteraction ||
            (!props.enabledGroupedAreasInteraction && !grouped.value));
        const color = computed(() => getVirtualStrokeColor(props.shape, onHover.value, selected.value));
        const weight = computed(() => getStrokeWeight(selected.value, onHover.value, experiment.vesselType));
        const sortAlphaNum = (data, keyName) => data.sort((a, b) => a[keyName].localeCompare(b[keyName], "en", { numeric: true }));
        const getLux3ExperimentId = () => {
            var _a, _b;
            let lux3ExperimentId = "";
            if (props.shape.type === ShapeEnum.CIRCLE) {
                lux3ExperimentId = props.shape.lux3ExperimentId;
            }
            else if (props.shape.type === ShapeEnum.WELL) {
                const roisInWell = sortAlphaNum(virtualWellPlateProvider.getAllROIsInWell(props.shape.id), "id");
                lux3ExperimentId = (_b = (_a = roisInWell[0]) === null || _a === void 0 ? void 0 : _a.lux3ExperimentId) !== null && _b !== void 0 ? _b : "";
            }
            return lux3ExperimentId;
        };
        const select = () => {
            if (isFullStitch.value) {
                selector.selectItems([props.shape.id]);
            }
            else {
                const items = rois.value.map(roi => roi.id);
                selector.selectItems(items, { ignoreGroups: props.ignoreGroups });
            }
            onHover.value = false;
            onHover.value = true;
        };
        const handleClick = function () {
            if (interactive.value) {
                select();
            }
        };
        const handleDoubleClick = function () {
            if (interactive.value && getLux3ExperimentId()) {
                emit("openLux3Experiment", getLux3ExperimentId());
            }
        };
        function getBoundingBoxCenter(rois) {
            if (!rois || rois.length === 0) {
                console.warn("No ROIs provided to calculate bounding box.");
                return null;
            }
            // Initialize bounding box limits
            let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
            rois.forEach((roi) => {
                if (!roi.bounds) {
                    console.error("ROI missing bounds property:", roi);
                    return;
                }
                roi.bounds.forEach((point) => {
                    if (point.length === 2) {
                        const [lat, lng] = point;
                        if (lat < minLat)
                            minLat = lat;
                        if (lat > maxLat)
                            maxLat = lat;
                        if (lng < minLng)
                            minLng = lng;
                        if (lng > maxLng)
                            maxLng = lng;
                    }
                    else {
                        console.error("Invalid point in ROI bounds:", point);
                    }
                });
            });
            const centerLat = (minLat + maxLat) / 2;
            const centerLng = (minLng + maxLng) / 2;
            // Flipped the order of lat and lng since the wellplate is flipped from the application
            return new LatLng(centerLng, centerLat);
        }
        function updateROIsWithBoundingBoxCenter() {
            const boundingBoxCenter = getBoundingBoxCenter(rois.value);
            if (!boundingBoxCenter) {
                console.warn("Bounding box center is null. ROIs will not be updated.");
                return;
            }
            // Calculate the difference between the shape center and the bounding box center
            const diffLat = props.shape.center.lat - boundingBoxCenter.lat;
            const diffLng = props.shape.center.lng - boundingBoxCenter.lng;
            // Update each ROI using the moveShape() function
            rois.value.forEach((roi) => {
                if (!roi.setBounds) {
                    console.error("ROI is missing setBounds method:", roi);
                    return;
                }
                // Calculate the new bounds by applying the difference
                const newBounds = roi.bounds.map(([lat, lng]) => [
                    lat + diffLng,
                    lng + diffLat
                ]);
                const newCenter = [roi.center.lat + diffLat, roi.center.lng + diffLng];
                roi.changeSize(newBounds, newCenter);
            });
        }
        updateROIsWithBoundingBoxCenter();
        return {
            onHover,
            rois,
            virtualWellPlateProvider,
            ShapeEnum,
            selected,
            color,
            interactive,
            handleClick,
            handleDoubleClick,
            weight,
            hasAlertConditionMet,
            showAlert
        };
    }
});
